import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Plus, Trash2, Repeat } from "lucide-react";
import { toast } from "sonner";
import { saas } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, kr } from "@/components/Layout";
import { FormDialog } from "@/components/FormDialog";

const JOB_STATUS = [
  { value: "planlagt", label: "Planlagt" }, { value: "pagar", label: "Pågår" }, { value: "fullfort", label: "Fullført" },
  { value: "avlyst", label: "Avlyst" }, { value: "krever_oppfolging", label: "Krever oppfølging" },
];
const COLORS = { planlagt: "border-l-blue-500", pagar: "border-l-amber-500", fullfort: "border-l-green-600", avlyst: "border-l-gray-400", krever_oppfolging: "border-l-red-500" };
const DAYS = ["Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const monday = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };

export default function Calendar() {
  const { user } = useAuth();
  const isAdmin = user?.role !== "employee";
  const [start, setStart] = useState(monday(new Date()));
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [edit, setEdit] = useState(null);

  const days = useMemo(() => [...Array(7)].map((_, i) => { const d = new Date(start); d.setDate(d.getDate() + i); return d; }), [start]);
  const load = useCallback(() => saas.jobs.list({ from_date: iso(days[0]), to_date: iso(days[6]) }).then(setJobs), [days]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    saas.customers.list().then(setCustomers);
    if (isAdmin) saas.employees.list().then(setEmployees).catch(() => {});
  }, [isAdmin]);

  const shift = (n) => { const d = new Date(start); d.setDate(d.getDate() + n * 7); setStart(d); };
  const empName = (id) => employees.find((e) => e.id === id)?.name;

  const fields = [
    { k: "title", label: "Tittel", full: true },
    { k: "customer_id", label: "Kunde", type: "select", options: [{ value: "", label: "– Ingen –" }, ...customers.map((c) => ({ value: c.id, label: c.name }))] },
    { k: "address", label: "Adresse" }, { k: "date", label: "Dato", type: "date" },
    { k: "start_time", label: "Start", type: "time" }, { k: "end_time", label: "Slutt", type: "time" },
    { k: "price", label: "Pris (eks. mva)", type: "number" },
    ...(isAdmin ? [{ k: "assigned_to", label: "Ansatt", type: "select", options: [{ value: "", label: "– Ikke tildelt –" }, ...employees.map((e) => ({ value: e.id, label: e.name }))] }] : []),
    { k: "status", label: "Status", type: "select", options: JOB_STATUS },
    ...(!edit?.id ? [
      { k: "freq", label: "Gjentakelse", type: "select", options: [{ value: "", label: "Ingen" }, { value: "weekly", label: "Hver uke" }, { value: "biweekly", label: "Annenhver uke" }, { value: "monthly", label: "Hver måned" }, { value: "daily", label: "Hver dag" }] },
      { k: "until", label: "Gjenta til", type: "date" },
    ] : []),
    { k: "instructions", label: "Instrukser", type: "textarea" },
  ];

  const openNew = (d) => setEdit({ title: "", customer_id: "", address: "", date: iso(d), start_time: "08:00", end_time: "10:00", price: 0, assigned_to: "", status: "planlagt", freq: "", until: "", instructions: "" });

  const save = async () => {
    if (!edit.title?.trim()) return toast.error("Tittel mangler");
    const { freq, until, ...rest } = edit;
    const body = { ...rest, customer_id: rest.customer_id || null, assigned_to: rest.assigned_to || null, price: Number(rest.price) || 0 };
    if (!body.address && body.customer_id) body.address = customers.find((c) => c.id === body.customer_id)?.address || "";
    if (edit.id) {
      isAdmin ? await saas.jobs.update(edit.id, body) : await saas.jobStatus(edit.id, { status: body.status });
    } else {
      if (freq && until) body.recurrence = { freq, until };
      const r = await saas.jobs.create(body);
      toast.success(`${r.created} oppdrag opprettet`);
    }
    setEdit(null);
    load();
  };
  const remove = async (all) => {
    await saas.jobs.remove(edit.id, all ? { scope_all: true } : {});
    setEdit(null);
    load();
  };

  return (
    <div className="p-4 sm:p-8" data-testid="calendar-page">
      <PageHeader title="Kalender" subtitle={`Uke fra ${days[0].toLocaleDateString("nb-NO")} til ${days[6].toLocaleDateString("nb-NO")}`}>
        <Button variant="outline" size="icon" data-testid="calendar-prev-week" onClick={() => shift(-1)}><ChevronLeft className="h-4 w-4" /></Button>
        <Button variant="outline" data-testid="calendar-today" onClick={() => setStart(monday(new Date()))}>I dag</Button>
        <Button variant="outline" size="icon" data-testid="calendar-next-week" onClick={() => shift(1)}><ChevronRight className="h-4 w-4" /></Button>
        {isAdmin && <Button data-testid="add-job-button" onClick={() => openNew(new Date())} className="gap-1.5"><Plus className="h-4 w-4" /> Nytt oppdrag</Button>}
      </PageHeader>
      <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
        {days.map((d, i) => {
          const dayJobs = jobs.filter((j) => j.date === iso(d)).sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
          const today = iso(d) === iso(new Date());
          return (
            <div key={i} data-testid={`calendar-day-${iso(d)}`} className={`rounded-xl border bg-card p-2 min-h-[160px] ${today ? "border-primary" : "border-border"}`}>
              <button disabled={!isAdmin} onClick={() => openNew(d)} className="w-full text-left mb-2 text-xs font-bold uppercase text-muted-foreground hover:text-primary">
                {DAYS[i]} <span className={today ? "text-primary" : ""}>{d.getDate()}.{d.getMonth() + 1}</span>
              </button>
              <div className="space-y-1.5">
                {dayJobs.map((j) => (
                  <button key={j.id} data-testid={`job-${j.id}`} onClick={() => setEdit({ ...j, customer_id: j.customer_id || "", assigned_to: j.assigned_to || "" })}
                    className={`w-full text-left rounded-md border border-border border-l-4 ${COLORS[j.status] || ""} bg-background p-1.5 hover:shadow-sm`}>
                    <div className="text-[11px] font-bold">{j.start_time}–{j.end_time} {j.series_id && <Repeat className="inline h-3 w-3" />}</div>
                    <div className="text-xs font-semibold truncate">{j.title}</div>
                    {j.assigned_to && <div className="text-[10px] text-muted-foreground truncate">{empName(j.assigned_to)}</div>}
                    {j.price > 0 && <div className="text-[10px] text-muted-foreground">{kr(j.price)}</div>}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {edit && (
        <FormDialog open onOpenChange={() => setEdit(null)} title={edit.id ? "Oppdrag" : "Nytt oppdrag"} testid="job-dialog"
          fields={isAdmin ? fields : fields.filter((f) => f.k === "status")} value={edit} onChange={setEdit} onSubmit={save}>
          {edit.id && isAdmin && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="text-destructive gap-1" data-testid="delete-job-button" onClick={() => remove(false)}><Trash2 className="h-4 w-4" /> Slett</Button>
              {edit.series_id && <Button variant="outline" size="sm" className="text-destructive" data-testid="delete-series-button" onClick={() => remove(true)}>Slett denne og fremtidige</Button>}
            </div>
          )}
        </FormDialog>
      )}
    </div>
  );
}
